import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Download, Printer, CheckCircle, XCircle, Send, Truck,
  Clock, FileText, User, Building2, Calendar, Hash, Mail, Phone, Car,
  AlertTriangle, ShieldCheck, UserCheck, Trash2, MailPlus, MessageCircle, Copy
} from "lucide-react";

const STATUS_META = {
  borrador: { label: "Borrador", color: "bg-slate-100 text-slate-600 border-slate-200", icon: FileText, dot: "bg-slate-400" },
  emitido: { label: "Emitido", color: "bg-blue-50 text-blue-700 border-blue-200", icon: Send, dot: "bg-blue-500" },
  enviado: { label: "Enviado", color: "bg-violet-50 text-violet-700 border-violet-200", icon: Mail, dot: "bg-violet-500" },
  entregado: { label: "Entregado", color: "bg-cyan-50 text-cyan-700 border-cyan-200", icon: Truck, dot: "bg-cyan-500" },
  canjeado: { label: "Canjeado", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle, dot: "bg-emerald-500" },
  caducado: { label: "Caducado", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock, dot: "bg-amber-500" },
  anulado: { label: "Anulado", color: "bg-red-50 text-red-700 border-red-200", icon: XCircle, dot: "bg-red-500" },
};

const ACTION_META = {
  created: { label: "Creado", color: "bg-slate-500" },
  issued: { label: "Emitido", color: "bg-blue-500" },
  sent: { label: "Enviado", color: "bg-violet-500" },
  delivered: { label: "Entregado", color: "bg-cyan-500" },
  redeemed: { label: "Canjeado", color: "bg-emerald-500" },
  cancelled: { label: "Anulado", color: "bg-red-500" },
  pdf_generated: { label: "PDF generado", color: "bg-slate-400" },
  emailed: { label: "Enviado por email", color: "bg-violet-500" },
  whatsapped: { label: "Enviado por WhatsApp", color: "bg-green-500" },
  email_redeem_sent: { label: "Email de canje enviado", color: "bg-emerald-400" },
  reminder_sent: { label: "Recordatorio enviado", color: "bg-amber-400" },
  config_updated: { label: "Config actualizada", color: "bg-slate-400" },
  template_deleted: { label: "Plantilla eliminada", color: "bg-red-400" },
};

export default function VoucherDetail() {
  const { axiosAuth } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [actionNotes, setActionNotes] = useState("");
  const [actionCenter, setActionCenter] = useState("");
  const [actionAdvisor, setActionAdvisor] = useState("");
  const [centers, setCenters] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [saving, setSaving] = useState(false);

  // Email PDF dialog
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // WhatsApp dialog
  const [whatsappDialog, setWhatsappDialog] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState("");
  const [whatsappMsg, setWhatsappMsg] = useState("");

  // Duplicate dialog
  const [duplicateDialog, setDuplicateDialog] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  const load = async () => {
    try {
      const r = await axiosAuth().get(`/vouchers/detail/${id}`);
      setVoucher(r.data);
    } catch { toast.error("Error al cargar bono"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    axiosAuth().get("/vouchers/config").then(r => {
      setCenters(r.data.centers || []);
      setSellers(r.data.sellers || []);
    }).catch(() => {});
  }, [axiosAuth]);

  const requestAction = (status) => {
    setActionDialog(status);
    setActionNotes("");
    setActionCenter("");
    setActionAdvisor("");
    setConfirmDialog(false);
  };

  const changeStatus = async () => {
    setSaving(true);
    try {
      await axiosAuth().put(`/vouchers/${id}/status`, {
        status: actionDialog, notes: actionNotes, center: actionCenter, advisor: actionAdvisor
      });
      toast.success(`Estado cambiado a ${STATUS_META[actionDialog]?.label || actionDialog}`);
      setActionDialog(null); setConfirmDialog(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar estado");
    } finally { setSaving(false); }
  };

  const handleConfirmAction = () => {
    if (actionDialog === "anulado" || actionDialog === "canjeado") {
      setConfirmDialog(true);
    } else {
      changeStatus();
    }
  };

  const downloadPdf = async () => {
    try {
      const r = await axiosAuth().get(`/vouchers/pdf/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `bono_${voucher.code.replace(/-/g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar PDF"); }
  };

  const printPdf = async () => {
    try {
      const r = await axiosAuth().get(`/vouchers/pdf/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url);
    } catch { toast.error("Error al imprimir"); }
  };

  const openEmailDialog = () => {
    setEmailTo(voucher.client_email || "");
    setEmailSubject(`Tu bono Ascauto — ${voucher.code}`);
    setEmailBody("");
    setEmailDialog(true);
  };

  const handleSendEmail = async () => {
    if (!emailTo || !emailTo.includes("@")) { toast.error("Email no valido"); return; }
    setSendingEmail(true);
    try {
      await axiosAuth().post(`/vouchers/${id}/send-email`, {
        to_email: emailTo, subject: emailSubject, body: emailBody
      });
      toast.success(`PDF enviado a ${emailTo}`);
      setEmailDialog(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar email. El bono no se ha visto afectado.");
    } finally { setSendingEmail(false); }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== "ELIMINAR") { toast.error("Escribe ELIMINAR para confirmar"); return; }
    setDeleting(true);
    try {
      await axiosAuth().delete(`/vouchers/${id}`);
      toast.success("Bono eliminado permanentemente");
      navigate("/admin/atenciones/bonos");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    } finally { setDeleting(false); }
  };

  const openWhatsappDialog = () => {
    const phone = (voucher.client_phone || "").replace(/\s/g, "");
    setWhatsappPhone(phone);
    const msg = `Hola${voucher.client_name ? ` ${voucher.client_name}` : ""}, le enviamos su bono de atencion comercial de Ascauto (${voucher.code}). Puede canjearlo hasta el ${voucher.expiry_date || "la fecha indicada"} en cualquiera de nuestros centros. Gracias por su confianza.`;
    setWhatsappMsg(msg);
    setWhatsappDialog(true);
  };

  const sendWhatsapp = async () => {
    if (!whatsappPhone) { toast.error("Numero de telefono requerido"); return; }
    let phone = whatsappPhone.replace(/\s/g, "").replace(/[^0-9+]/g, "");
    if (!phone.startsWith("+")) phone = "+34" + phone;
    const encoded = encodeURIComponent(whatsappMsg);
    window.open(`https://wa.me/${phone.replace("+", "")}?text=${encoded}`, "_blank");
    try {
      await axiosAuth().post(`/vouchers/${id}/whatsapp-event`, { phone: whatsappPhone, message: whatsappMsg });
    } catch {}
    toast.success("WhatsApp abierto. Descarga el PDF para adjuntarlo manualmente.");
    setWhatsappDialog(false);
    await load();
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const r = await axiosAuth().post(`/vouchers/${id}/duplicate`);
      toast.success(`Bono duplicado: ${r.data.code}. Revisa los datos antes de emitir.`);
      setDuplicateDialog(false);
      navigate(`/admin/atenciones/bonos/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al duplicar bono");
    } finally { setDuplicating(false); }
  };

  if (loading || !voucher) return <div className="p-8 text-center text-sm text-slate-400">Cargando...</div>;

  const sm = STATUS_META[voucher.status] || STATUS_META.borrador;
  const StatusIcon = sm.icon;
  const transitions = {
    borrador: [{ to: "emitido", label: "Emitir", variant: "default" }],
    emitido: [
      { to: "entregado", label: "Marcar entregado", variant: "outline" },
      { to: "canjeado", label: "Canjear", variant: "success" },
      { to: "anulado", label: "Anular", variant: "destructive" },
    ],
    enviado: [
      { to: "entregado", label: "Marcar entregado", variant: "outline" },
      { to: "canjeado", label: "Canjear", variant: "success" },
      { to: "anulado", label: "Anular", variant: "destructive" },
    ],
    entregado: [
      { to: "canjeado", label: "Canjear", variant: "success" },
      { to: "anulado", label: "Anular", variant: "destructive" },
    ],
  };
  const availableActions = transitions[voucher.status] || [];
  const isRedeemAction = actionDialog === "canjeado" || actionDialog === "entregado";

  const getActionBtnClass = (variant) => {
    switch (variant) {
      case "success": return "bg-emerald-600 text-white hover:bg-emerald-700";
      case "destructive": return "bg-red-600 text-white hover:bg-red-700";
      default: return "bg-slate-900 text-white hover:bg-slate-800";
    }
  };

  const fields = [
    { icon: User, label: "Cliente", value: voucher.client_name },
    { icon: Car, label: "Matricula", value: voucher.plate, mono: true },
    { icon: Phone, label: "Telefono", value: voucher.client_phone },
    { icon: Mail, label: "Email", value: voucher.client_email },
    { icon: Building2, label: "Centro emisor", value: voucher.center },
    { icon: User, label: "Emisor", value: voucher.issuer_name },
    { icon: Calendar, label: "Fecha emision", value: voucher.issue_date },
    { icon: Calendar, label: "Fecha caducidad", value: voucher.expiry_date },
    { icon: Hash, label: "Valor estimado", value: voucher.estimated_value ? `${voucher.estimated_value} EUR` : null },
  ];

  return (
    <div className="space-y-6" data-testid="voucher-detail">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="h-9 w-9 p-0 rounded-sm mt-0.5" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-mono font-bold text-slate-900 tracking-tight" data-testid="voucher-code">{voucher.code}</h1>
            <Badge variant="outline" className={`h-6 text-[10px] rounded-sm font-semibold px-2.5 border ${sm.color}`}>
              <StatusIcon className="w-3.5 h-3.5 mr-1" /> {sm.label}
            </Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1">{voucher.template_snapshot?.name || voucher.type}</p>
        </div>
        <div className="flex gap-2 flex-shrink-0 flex-wrap">
          <Button variant="outline" size="sm" onClick={downloadPdf} className="rounded-sm text-[10px] uppercase h-9 px-4 tracking-wider font-semibold" data-testid="detail-pdf-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={printPdf} className="rounded-sm text-[10px] uppercase h-9 px-4 tracking-wider font-semibold">
            <Printer className="w-3.5 h-3.5 mr-1.5" /> Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={openEmailDialog} className="rounded-sm text-[10px] uppercase h-9 px-4 tracking-wider font-semibold text-violet-700 border-violet-200 hover:bg-violet-50" data-testid="send-email-btn">
            <MailPlus className="w-3.5 h-3.5 mr-1.5" /> Email
          </Button>
          <Button variant="outline" size="sm" onClick={openWhatsappDialog} className="rounded-sm text-[10px] uppercase h-9 px-4 tracking-wider font-semibold text-green-700 border-green-200 hover:bg-green-50" data-testid="send-whatsapp-btn">
            <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> WhatsApp
          </Button>
          <Button variant="outline" size="sm" onClick={() => setDuplicateDialog(true)} className="rounded-sm text-[10px] uppercase h-9 px-4 tracking-wider font-semibold text-slate-600 border-slate-200 hover:bg-slate-50" data-testid="duplicate-voucher-btn">
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Duplicar
          </Button>
          {availableActions.map(a => (
            <Button key={a.to} size="sm" onClick={() => requestAction(a.to)}
              className={`${getActionBtnClass(a.variant)} rounded-sm text-[10px] uppercase h-9 px-4 tracking-wider font-semibold`} data-testid={`action-${a.to}`}>
              {a.label}
            </Button>
          ))}
          <Button variant="ghost" size="sm" onClick={() => { setDeleteDialog(true); setDeleteConfirmText(""); }}
            className="h-9 w-9 p-0 rounded-sm text-slate-300 hover:text-red-600 hover:bg-red-50" data-testid="delete-voucher-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Main info card */}
        <div className="lg:col-span-2 space-y-5">
          <Card className="border-slate-200 rounded-sm overflow-hidden">
            <div className="h-1" style={{ backgroundColor: voucher.template_snapshot?.bar_color || "#0f172a" }} />
            <div className="h-0.5" style={{ backgroundColor: voucher.template_snapshot?.accent_color || "#dc2626" }} />
            <CardHeader className="pb-3 pt-5">
              <CardTitle className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Datos del bono</CardTitle>
            </CardHeader>
            <CardContent className="pb-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
                {fields.map((f, i) => (
                  <div key={i} data-testid={`field-${f.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 font-bold flex items-center gap-1.5 mb-1">
                      <f.icon className="w-3.5 h-3.5" /> {f.label}
                    </p>
                    <p className={`text-sm font-semibold ${f.value ? "text-slate-800" : "text-slate-300"} ${f.mono ? "font-mono" : ""}`}>
                      {f.value || "—"}
                    </p>
                  </div>
                ))}
              </div>

              {voucher.observations && (
                <div className="mt-5 pt-4 border-t border-slate-100">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400 font-bold mb-1">Observaciones</p>
                  <p className="text-sm text-slate-700 leading-relaxed">{voucher.observations}</p>
                </div>
              )}

              {voucher.internal_reason && (
                <div className="mt-4 bg-amber-50/80 border border-amber-200 rounded-sm p-4">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-amber-700 font-bold mb-1 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> Motivo interno
                  </p>
                  <p className="text-sm text-amber-800">{voucher.internal_reason}</p>
                </div>
              )}

              {(voucher.status === "canjeado" && voucher.redeemed_at) && (
                <div className="mt-4 bg-emerald-50/80 border border-emerald-200 rounded-sm p-4">
                  <p className="text-[10px] uppercase tracking-[0.12em] text-emerald-700 font-bold mb-1 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5" /> Datos de canje
                  </p>
                  {voucher.redeem_notes && <p className="text-sm text-emerald-800 mb-1">{voucher.redeem_notes}</p>}
                  <div className="flex flex-wrap gap-3 text-[11px] text-emerald-600 mt-2">
                    {voucher.redeemed_by && <span className="flex items-center gap-1"><UserCheck className="w-3 h-3" /> {voucher.redeemed_by}</span>}
                    {voucher.redeem_center && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {voucher.redeem_center}</span>}
                    {voucher.redeem_advisor && <span className="flex items-center gap-1"><User className="w-3 h-3" /> Asesor: {voucher.redeem_advisor}</span>}
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {voucher.redeemed_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Timeline card */}
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3 pt-5">
            <CardTitle className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">Historial de eventos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative" data-testid="voucher-timeline">
              {(voucher.events || []).map((ev, i) => {
                const meta = ACTION_META[ev.action] || { label: ev.action, color: "bg-slate-400" };
                return (
                  <div key={ev.id || i} className="flex gap-3 pb-5 last:pb-0">
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full ${meta.color} flex-shrink-0 ring-2 ring-white shadow-sm`} />
                      {i < (voucher.events?.length || 0) - 1 && <div className="w-px flex-1 bg-slate-200 mt-1" />}
                    </div>
                    <div className="flex-1 -mt-0.5 min-w-0">
                      <p className="text-xs font-bold text-slate-800">{meta.label}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{ev.user_name}{ev.center ? ` — ${ev.center}` : ""}</p>
                      {ev.detail && <p className="text-[11px] text-slate-400 mt-0.5 break-words">{ev.detail}</p>}
                      <p className="text-[10px] text-slate-300 mt-1 font-mono">{ev.timestamp?.slice(0, 16).replace("T", " ")}</p>
                    </div>
                  </div>
                );
              })}
              {(!voucher.events || voucher.events.length === 0) && (
                <p className="text-xs text-slate-400 text-center py-6">Sin eventos registrados</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action dialog */}
      <Dialog open={!!actionDialog && !confirmDialog} onOpenChange={o => { if (!o) setActionDialog(null); }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="action-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">
              {actionDialog === "canjeado" ? "Canjear bono" : actionDialog === "anulado" ? "Anular bono" : `Marcar como ${actionDialog}`}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {actionDialog === "canjeado"
                ? "Introduce los datos del canje. Si el cliente tiene email, recibira una confirmacion automatica."
                : actionDialog === "anulado"
                  ? "Esta accion es irreversible. El bono quedara anulado permanentemente."
                  : "Cambia el estado del bono."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {isRedeemAction && (
              <>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Centro</Label>
                  <Select value={actionCenter || "none"} onValueChange={v => setActionCenter(v === "none" ? "" : v)}>
                    <SelectTrigger className="rounded-sm h-9 border-slate-200 mt-1" data-testid="action-center-select"><SelectValue placeholder="Seleccionar centro" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin especificar</SelectItem>
                      {centers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Asesor</Label>
                  <Select value={actionAdvisor || "none"} onValueChange={v => setActionAdvisor(v === "none" ? "" : v)}>
                    <SelectTrigger className="rounded-sm h-9 border-slate-200 mt-1" data-testid="action-advisor-select"><SelectValue placeholder="Seleccionar asesor" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin especificar</SelectItem>
                      {sellers.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Notas</Label>
              <Textarea value={actionNotes} onChange={e => setActionNotes(e.target.value)}
                placeholder="Notas opcionales..." className="rounded-sm border-slate-200 mt-1" rows={2} data-testid="action-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)} className="rounded-sm">Cancelar</Button>
            <Button onClick={handleConfirmAction} disabled={saving}
              className={`${getActionBtnClass(actionDialog === "anulado" ? "destructive" : actionDialog === "canjeado" ? "success" : "default")} rounded-sm`}
              data-testid="confirm-action-btn">
              {saving ? "Guardando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Double confirmation dialog for destructive/canje actions */}
      <Dialog open={confirmDialog} onOpenChange={o => { if (!o) setConfirmDialog(false); }}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="double-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${actionDialog === "anulado" ? "text-red-600" : "text-emerald-600"}`} />
              Confirmar {actionDialog === "anulado" ? "anulacion" : "canje"}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-2">
              {actionDialog === "anulado"
                ? `Estas a punto de ANULAR el bono ${voucher.code}. Esta accion no se puede deshacer.`
                : `Estas a punto de CANJEAR el bono ${voucher.code}. El cliente sera notificado por email si tiene uno registrado.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setConfirmDialog(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={changeStatus} disabled={saving}
              className={`${actionDialog === "anulado" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"} text-white rounded-sm`}
              data-testid="final-confirm-btn">
              {saving ? "Procesando..." : `Si, ${actionDialog === "anulado" ? "anular" : "canjear"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send PDF by email dialog */}
      <Dialog open={emailDialog} onOpenChange={o => { if (!o) setEmailDialog(false); }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="send-email-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <MailPlus className="w-5 h-5 text-violet-600" /> Enviar PDF por email
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Se adjuntara el PDF del bono {voucher.code} automaticamente al email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Destinatario *</Label>
              <Input value={emailTo} onChange={e => setEmailTo(e.target.value)}
                placeholder="cliente@email.com" className="rounded-sm h-9 border-slate-200 mt-1" data-testid="email-to-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Asunto</Label>
              <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
                className="rounded-sm h-9 border-slate-200 mt-1" data-testid="email-subject-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Mensaje adicional (opcional)</Label>
              <Textarea value={emailBody} onChange={e => setEmailBody(e.target.value)}
                placeholder="Escribe aqui un mensaje personalizado..." className="rounded-sm border-slate-200 mt-1" rows={3} data-testid="email-body-input" />
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-[11px] text-slate-500">
              <p className="font-bold text-[10px] uppercase tracking-[0.12em] text-slate-400 mb-1">Adjunto</p>
              <p className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-red-600" /> bono_{voucher.code.replace(/-/g, "_")}.pdf</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail || !emailTo}
              className="bg-violet-600 text-white hover:bg-violet-700 rounded-sm" data-testid="confirm-send-email-btn">
              <Send className="w-3.5 h-3.5 mr-1.5" /> {sendingEmail ? "Enviando..." : "Enviar email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp dialog */}
      <Dialog open={whatsappDialog} onOpenChange={o => { if (!o) setWhatsappDialog(false); }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="whatsapp-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-600" /> Enviar por WhatsApp
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Se abrira WhatsApp con el mensaje cargado. Descarga el PDF para adjuntarlo manualmente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Numero de telefono *</Label>
              <Input value={whatsappPhone} onChange={e => setWhatsappPhone(e.target.value)}
                placeholder="600123456" className="rounded-sm h-9 border-slate-200 mt-1" data-testid="whatsapp-phone-input" />
              <p className="text-[10px] text-slate-400 mt-1">Se anadira +34 si no incluye prefijo internacional</p>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-slate-600">Mensaje</Label>
              <Textarea value={whatsappMsg} onChange={e => setWhatsappMsg(e.target.value)}
                className="rounded-sm border-slate-200 mt-1 text-sm" rows={4} data-testid="whatsapp-msg-input" />
            </div>
            <div className="bg-green-50 border border-green-200 rounded-sm p-3 text-[11px] text-green-700">
              <p className="font-bold text-[10px] uppercase tracking-[0.12em] text-green-600 mb-1">Flujo de envio</p>
              <ol className="list-decimal list-inside space-y-0.5">
                <li>Se abrira WhatsApp Web/App con el mensaje cargado</li>
                <li>Descarga el PDF por separado para adjuntarlo</li>
                <li>Envia el mensaje y adjunta el PDF</li>
              </ol>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={downloadPdf} className="rounded-sm" data-testid="whatsapp-download-pdf-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Descargar PDF
            </Button>
            <Button variant="outline" onClick={() => setWhatsappDialog(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={sendWhatsapp} disabled={!whatsappPhone}
              className="bg-green-600 text-white hover:bg-green-700 rounded-sm" data-testid="confirm-send-whatsapp-btn">
              <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Abrir WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete voucher dialog (double confirmation: type ELIMINAR) */}
      <Dialog open={deleteDialog} onOpenChange={o => { if (!o) setDeleteDialog(false); }}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="delete-voucher-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-600" /> Eliminar bono
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-2">
              Estas a punto de eliminar permanentemente el bono <strong className="font-mono">{voucher.code}</strong> de <strong>{voucher.client_name}</strong>.
              Esta accion es irreversible y se perdera toda la informacion asociada.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-[10px] uppercase tracking-[0.12em] font-bold text-red-600">Escribe ELIMINAR para confirmar</Label>
            <Input value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder="ELIMINAR" className="rounded-sm h-9 border-red-200 mt-1 font-mono uppercase" data-testid="delete-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={handleDelete} disabled={deleting || deleteConfirmText !== "ELIMINAR"}
              className="bg-red-600 text-white hover:bg-red-700 rounded-sm disabled:opacity-40" data-testid="confirm-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> {deleting ? "Eliminando..." : "Eliminar permanentemente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate voucher dialog */}
      <Dialog open={duplicateDialog} onOpenChange={o => { if (!o) setDuplicateDialog(false); }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="duplicate-voucher-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-bold flex items-center gap-2">
              <Copy className="w-5 h-5 text-slate-700" /> Duplicar bono
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-2">
              Se creara un nuevo bono en estado <strong>borrador</strong> con los datos reutilizables de <strong className="font-mono">{voucher.code}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-2">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Datos que se copiaran</p>
            <div className="bg-slate-50 rounded-sm p-3 text-xs text-slate-600 space-y-1">
              {voucher.client_name && <p><span className="text-slate-400 font-semibold">Cliente:</span> {voucher.client_name}</p>}
              {voucher.plate && <p><span className="text-slate-400 font-semibold">Matricula:</span> <span className="font-mono">{voucher.plate}</span></p>}
              {voucher.center && <p><span className="text-slate-400 font-semibold">Centro:</span> {voucher.center}</p>}
              <p><span className="text-slate-400 font-semibold">Plantilla:</span> {voucher.template_snapshot?.name || voucher.type}</p>
              {voucher.observations && <p><span className="text-slate-400 font-semibold">Observaciones:</span> {voucher.observations}</p>}
              {voucher.estimated_value > 0 && <p><span className="text-slate-400 font-semibold">Valor:</span> {voucher.estimated_value} EUR</p>}
            </div>
            <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Datos nuevos</p>
            <div className="bg-emerald-50 rounded-sm p-3 text-xs text-emerald-700 space-y-1">
              <p>Codigo unico nuevo generado automaticamente</p>
              <p>Estado inicial: <strong>Borrador</strong></p>
              <p>Nueva fecha de caducidad calculada</p>
              <p>Historial: registra origen del duplicado</p>
            </div>
            <p className="text-[10px] text-slate-400 italic">No se copia: historial anterior, estado de canje, motivo interno.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateDialog(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={handleDuplicate} disabled={duplicating}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="confirm-duplicate-btn">
              <Copy className="w-3.5 h-3.5 mr-1.5" /> {duplicating ? "Duplicando..." : "Duplicar y revisar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
